import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { getOrCreateStore } from 'state/storeProvider';
import Map from 'components/map/Map';
import CarouselImage from 'components/carousel/CarouselImage';
import MapMarkerStars from 'components/map/components/mapMarker/MapMarkerComponent.Pin';
import MapMarkerPopup from 'components/map/components/mapMarker/MapMarkerPopupComponent';
import MapMarkerPopupPlaceholder from 'components/map/components/mapMarker/MapMarkerPopupPlaceholderComponent';
import getPackageLink from 'components/packageCard/utils/getPackageLink';
import { mediaQueries } from 'styles/utils/mediaQueries';
import getSelectedRooms from 'state/selectRooms/operations/getSelectedRooms';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import getPackageUniqueKey from 'components/packageCard/utils/getPackageUniqueKey';
import { POPULAR_HOTELS_PACKAGE } from 'components/packageCard/constants/packageTypes';
import { HOTEL_ADD_TO_FAVORITES } from 'routes/hotelPage/components/shareAndFavorites/constants/keys';
import { updateSavedPackages } from 'state/savedPackages/operations';
import logHotelSlugNotProvided from 'helpers/errors/logHotelSlugNotProvided';
import getImageSource from 'components/packageCard/utils/getImageSource';

const mapboxgl = process.browser ? require('mapbox-gl') : {};

const sourceHotelsId = 'hotels';
const layerClustersId = 'clusters';
const layerClusterCountId = 'cluster-count';
const layerUnclusteredPointId = 'unclustered-point';

class TemplatesMap extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isMapVisible: true,
    };

    this.mapContainerRef = React.createRef();
    this.bounds = process.browser && new mapboxgl.LngLatBounds();
    this.markers = {};
    this.mapZoom = 0;
    this.resizeOccurred = false;

    this.handleOnMapLoad = this.handleOnMapLoad.bind(this);
    this.handleOnMapMove = this.handleOnMapMove.bind(this);
    this.handleOnMapZoom = this.handleOnMapZoom.bind(this);
    this.handleOnMapResize = this.handleOnMapResize.bind(this);
    this.handleOnClusterClick = this.handleOnClusterClick.bind(this);
    this.handleOnMouseEnterCluster = this.handleOnMouseEnterCluster.bind(this);
    this.handleOnMouseLeaveCluster = this.handleOnMouseLeaveCluster.bind(this);
    this.toggleMapVisibility = this.toggleMapVisibility.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.selectedHotel !== this.props.selectedHotel) {
      if (prevProps.selectedHotel !== null) {
        const prevEl = document.getElementById(getMarkerId(prevProps.selectedHotel));
        if (prevEl) {
          prevEl.classList.remove('selected');
        }
      }

      if (this.props.selectedHotel !== null) {
        const currentEl = document.getElementById(getMarkerId(this.props.selectedHotel));
        if (currentEl) {
          currentEl.classList.add('selected');
        }
      }
    }
  }

  toggleMapVisibility() {
    this.setState((prevState) => ({
      isMapVisible: !prevState.isMapVisible,
    }));
  }

  handleOnMapLoad({ map }) {
    this.map = map;

    const hotelsSource = getHotelsSource(this.props.hotels, this.bounds);

    this.map.addSource(sourceHotelsId, hotelsSource);

    // clusters
    this.map.addLayer({
      id: layerClustersId,
      type: 'circle',
      source: sourceHotelsId,
      filter: ['has', 'point_count'],
      paint: {
        'circle-radius': 20,
        'circle-color': this.props.colors.secondary.light,
        'circle-stroke-width': 2,
        'circle-stroke-color': this.props.colors.secondary.default,
      },
    });

    // clusters count text
    this.map.addLayer({
      id: layerClusterCountId,
      type: 'symbol',
      source: sourceHotelsId,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['Arial Unicode MS Bold'],
        'text-size': 12,
      },
      paint: {
        'text-color': this.props.colors.mapMarkers.text,
      },
    });

    // unclustered points
    this.map.addLayer({
      id: layerUnclusteredPointId,
      type: 'circle',
      source: sourceHotelsId,
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-opacity': 0,
      },
    });

    this.map.fitBounds(this.bounds, { padding: 65, animate: false });

    this.map.on('click', layerClustersId, this.handleOnClusterClick);
    this.map.on('mouseenter', layerClustersId, this.handleOnMouseEnterCluster);
    this.map.on('mouseleave', layerClustersId, this.handleOnMouseLeaveCluster);
  }

  handleOnMapMove(event) {
    if (this.map === undefined) {
      return;
    }

    const renderedFeatures = this.map.queryRenderedFeatures(undefined, { layers: [layerUnclusteredPointId] });

    if (renderedFeatures.length > 0) {
      const hotels = renderedFeatures.map((feature) => feature.properties.hotel);
      this.createMarkers(hotels);
    }
  }

  handleOnMapZoom(event) {
    if (this.map === undefined) {
      return;
    }

    const currentMapZoom = parseFloat(this.map.getZoom().toFixed(2));
    const renderedFeatures = this.map.queryRenderedFeatures(undefined, { layers: [layerUnclusteredPointId] });

    // on zoom out
    if (currentMapZoom < this.mapZoom) {
      const hotelsIds = renderedFeatures.map((feature) => feature.properties.id);
      this.removeMarkers(hotelsIds);
    }

    this.mapZoom = currentMapZoom;
  }

  handleOnMapResize(event) {
    if (this.map === undefined) {
      return;
    }

    this.resizeOccurred = true;
  }

  handleOnClusterClick(event) {
    const features = this.map.queryRenderedFeatures(event.point, { layers: [layerClustersId] });
    const clusterId = features[0].properties.cluster_id;

    this.map.getSource(sourceHotelsId).getClusterExpansionZoom(clusterId, (error, zoom) => {
      if (error) {
        return;
      }

      this.map.easeTo({
        center: features[0].geometry.coordinates,
        zoom: zoom,
      });

      this.mapZoom = parseFloat(zoom.toFixed(2));
    });
  }

  handleOnMouseEnterCluster() {
    this.map.getCanvas().style.cursor = 'pointer';
  }

  handleOnMouseLeaveCluster() {
    this.map.getCanvas().style.cursor = '';
  }

  createMarkers(hotels) {
    const store = getOrCreateStore();
    const theme = store.getState().theme;

    hotels.forEach((hotel) => {
      hotel = JSON.parse(hotel);

      //TODO : remove later - old route redirect for hotel page
      if (!hotel.hotelSlug) {
        logHotelSlugNotProvided(hotel.id);
        return null;
      }

      if (this.markers[hotel.id] === undefined) {
        const container = this.createMarker(hotel, store, theme);

        ReactDOM.render(
          React.createElement(MapMarkerStars, {
            store,
            theme,
            stars: hotel.stars,
          }),
          container
        );
      }
    });
  }

  createMarker(hotel, store, theme) {
    const el = document.createElement('div');
    el.id = getMarkerId(hotel.id);

    const coordinates = [hotel.location.coordinates.longitude, hotel.location.coordinates.latitude];

    el.onclick = () => this.map.flyTo({ center: coordinates, speed: 0.5, curve: 1 }, { manuallyTriggered: true });
    el.onmouseenter = function () {
      this.style.zIndex = '1';
    };
    el.onmouseleave = function () {
      this.style.zIndex = 'initial';
    };

    const popup = this.createPopup(hotel, store, theme);

    const marker = new mapboxgl.Marker(el).setLngLat(coordinates).setPopup(popup).addTo(this.map);

    this.markers[hotel.id] = marker;

    return el;
  }

  createPopup(hotel, store, theme) {
    var el = document.createElement('div');
    el.id = getPopupId(hotel.id);

    const popup = new mapboxgl.Popup({
      offset: 6,
      closeButton: false,
      maxWidth: 'none',
      anchor: 'bottom',
    }).setDOMContent(el);

    popup.on('open', this.handleOnPopupOpen.bind(this, hotel.id, store, theme));

    return popup;
  }

  removeMarkers(hotelsIds) {
    for (var key in this.markers) {
      if (this.markers.hasOwnProperty(key)) {
        if (hotelsIds.indexOf(key) === -1) {
          this.markers[key].remove();
          delete this.markers[key];
        }
      }
    }
  }

  handleOnPopupOpen(hotelId, store, theme) {
    // Get updated reference to the hotel
    const hotel = this.props.hotels.find(({ id }) => id === hotelId);

    if (!hotel) {
      console.error('Search Results Map Marker - could not find hotel result!', hotelId);
      window.Rollbar.error('Search Results Map Marker - could not find hotel result!', hotelId);
      return;
    }

    if (hotel.images && hotel.images.length > 0) {
      this.renderMapMarkerPopupComponent(hotel, hotel.images, store, theme);
    } else {
      ReactDOM.render(
        React.createElement(MapMarkerPopupPlaceholder, { theme }),
        document.getElementById(getPopupId(hotelId))
      );

      const hotelsWithMissingInfoAsString = this.getHotelIdsForHotelsWithMissingInfoNearMe(hotelId);
      const hotelsWithMissingInfo = hotelsWithMissingInfoAsString.map((item) => parseInt(item));
      hotelsWithMissingInfo.push(hotelId);
      //   const promise = this.props.searchResultsRef.current.loadPackagesManually(hotelId, hotelsWithMissingInfo);
      //   promise.then((response) => {
      //     this.renderMapMarkerPopupComponent(hotel, result, response.images, store, theme, response);
      //   });
    }
  }

  getHotelIdsForHotelsWithMissingInfoNearMe(clickedHotelId) {
    const renderedHotelMarkersHotelIds = Object.keys(this.markers);
    const renderedHotelMarkersHotelIdsWithMissingInfo = renderedHotelMarkersHotelIds.filter(
      (hotelId) => this.props.hotels[hotelId].images === undefined
    );

    // If all rendered markers with incomplete data are less than 20, will fetch data for all of them
    if (renderedHotelMarkersHotelIdsWithMissingInfo.length <= 20) {
      return renderedHotelMarkersHotelIdsWithMissingInfo;
    }

    // Get hotel ids of hotels with incomplete data near the marker that was clicked
    const _pos = this.markers[clickedHotelId]._pos;
    const geometry = [
      { x: _pos.x - 50, y: _pos.y - 50 },
      { x: _pos.x + 50, y: _pos.y + 50 },
    ];
    const hotelIdsWithMissingInfoNearClick = this.map
      .queryRenderedFeatures(geometry, { layers: [layerUnclusteredPointId] })
      .slice(0, 20)
      .map((feature) => feature.properties.id)
      .filter((hotelId) => this.props.hotels[hotelId].images === undefined);

    if (hotelIdsWithMissingInfoNearClick.length === 20) {
      return hotelIdsWithMissingInfoNearClick;
    }

    const removedDuplicates = renderedHotelMarkersHotelIdsWithMissingInfo.filter(
      (item) => hotelIdsWithMissingInfoNearClick.indexOf(item) === -1
    );

    return hotelIdsWithMissingInfoNearClick.concat(removedDuplicates).slice(0, 20);
  }

  renderMapMarkerPopupComponent(hotel, images, store, theme, promiseResponse) {
    const hotelImages = images.map((image, index) => (
      <CarouselImage
        key={`mapbox-popup-${hotel.id}-image-${index}`}
        src={getImageSource(image, { small: true })}
        small
      />
    ));

    const selectedRooms = this.props.getSelectedRooms();

    const searchQuery = {
      roomConfigurations: selectedRooms ? selectedRooms : [{ numberOfAdults: 2, childrenAges: [] }],
    };
    //TODO : remove later - old route redirect for hotel page
    const slug = getPackageLink(hotel.hotelSlug || hotel.slug, searchQuery);

    const hotelName = hotel.name !== null && hotel.name !== undefined ? hotel.name : promiseResponse.name;
    const hotelStars = hotel.stars !== null && hotel.stars !== undefined ? hotel.stars : promiseResponse.stars;
    const hotelScore = !promiseResponse ? hotel.score : promiseResponse.score;
    const hotelLocation = hotel.location && hotel.location.destination ? hotel.location : promiseResponse.location;
    const packageUniqueKey = getPackageUniqueKey(POPULAR_HOTELS_PACKAGE, hotel.id, 0, HOTEL_ADD_TO_FAVORITES);
    const savedPackage = {
      packageUniqueKey,
      packageLink: slug,
      selectedRooms,
      hotel: {
        ...hotel,
        name: hotelName,
        stars: hotelStars,
        score: hotelScore,
        images,
        location: hotelLocation,
        shortDescription: promiseResponse && promiseResponse.shortDescription,
      },
      packet: {},
      packageType: POPULAR_HOTELS_PACKAGE,
    };

    const popupElement = React.createElement(MapMarkerPopup, {
      store,
      theme,
      slug,
      hotelImages,
      hotelName,
      hotelStars: parseFloat(hotelStars),
      hotelScore: hotelScore / 10,
      departure: null,
      onClickViewInList: null,
      hotelPrice: null,
      simple: true, //simple prop means shorter component /without departure ,onClickView button ...
      onClickAddHearted: () => {
        this.props.addSavedPackages(savedPackage);
      },
      onClickRemoveHearted: () => {
        this.props.removeSavedPackages(packageUniqueKey);
      },
    });

    const container = document.getElementById(getPopupId(hotel.id));
    ReactDOM.unmountComponentAtNode(container);
    ReactDOM.render(popupElement, container);
  }

  render() {
    const config = {
      mapOptions: {
        maxZoom: 18,
        scrollZoom: false,
      },
      mapEvents: [
        {
          type: 'move',
          listener: this.handleOnMapMove,
          throttleDelay: 200,
        },
        {
          type: 'zoom',
          listener: this.handleOnMapZoom,
          throttleDelay: 200,
        },
        {
          type: 'resize',
          listener: this.handleOnMapResize,
        },
        {
          type: 'idle',
          evented: 'once',
          listener: this.handleOnMapMove,
        },
      ],
      controls: [
        {
          key: 'NavigationControl',
          position: 'top-left',
          options: { showCompass: false },
        },
      ],
      showMapStyleButton: true,
      showSearchTriggerToggle: false,
    };
    const { isMapVisible } = this.state;
    const mapContainer = <div className='mapbox-map-container' ref={this.mapContainerRef} />;
    return (
      <Root isMapVisible={isMapVisible}>
        <ToggleMap isMapVisible={isMapVisible} onClick={this.toggleMapVisibility}>
          <MapIcon icon='map-marked-alt' />
        </ToggleMap>
        <Map config={config} mapContainerRef={this.mapContainerRef} onMapLoad={this.handleOnMapLoad}>
          <TemplateMapStyled isMapVisible={isMapVisible}>{mapContainer}</TemplateMapStyled>
        </Map>
      </Root>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    selectedHotel: state.searchResultsMap.selectedHotel,
    colors: state.theme.colors,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getSelectedRooms() {
      return dispatch(getSelectedRooms());
    },
    addSavedPackages: function (packet) {
      dispatch(updateSavedPackages(null, packet));
    },
    removeSavedPackages: function (id) {
      dispatch(updateSavedPackages(id, null));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(TemplatesMap);

function getHotelsSource(hotels, bounds) {
  const features = generateGeoJSON(hotels, bounds);

  return {
    type: 'geojson',
    cluster: true,
    clusterMaxZoom: 10, // Max zoom to cluster points on
    clusterRadius: 35, // Radius of each cluster when clustering points,
    data: {
      type: 'FeatureCollection',
      features,
    },
  };
}

function generateGeoJSON(hotels, bounds) {
  let features = [];

  for (var key in hotels) {
    if (hotels.hasOwnProperty(key)) {
      const hotel = hotels[key];
      const coordinates = [hotel.location.coordinates.longitude, hotel.location.coordinates.latitude];
      const feature = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: coordinates,
        },
        properties: {
          id: hotel.id.toString(),
          hotel,
        },
      };

      features.push(feature);
      bounds.extend(coordinates);
    }
  }

  return features;
}

const getMarkerId = (hotelId) => `mapbox-marker-${hotelId}`;
const getPopupId = (hotelId) => `mapbox-marker-popup-${hotelId}`;

const TemplateMapStyled = styled.div`
  display: ${({ isMapVisible }) => (isMapVisible ? 'block' : 'none')};
  position: relative;
  height: 570px;
  border: 1px solid ${({ theme }) => theme.colors.primary.default};

  .mapbox-map-container {
    height: 570px;
    // transition: height 0.3s;
  }

  .mapboxgl-popup-content {
    padding: 0;
  }

  .selected {
    z-index: 4;

    span {
      background-color: ${({ theme }) => theme.colors.secondary.default};
      color: ${({ theme }) => theme.colors.text.white};

      &:after {
        border-color: transparent transparent transparent ${({ theme }) => theme.colors.secondary.default};
      }
    }
  }
  ${mediaQueries.below.l`
    height: 70vh;
    border: 0;

    .mapbox-map-container {
      height: 70vh;
    }

    #map-style-button {
      top: 10px;
      bottom: initial;
    }
  `}
`;

const Root = styled.div`
  position: relative;
`;

const ToggleMap = styled.div`
  position: absolute;
  top: ${({ isMapVisible }) => (isMapVisible ? '10px' : '-10px')};
  right: ${({ isMapVisible }) => (isMapVisible ? '10px' : '0')};
  z-index: 2;
  width: 40px;
  height: 40px;
  margin-right: 8px;
  background-color: ${({ theme, isMapVisible }) =>
    isMapVisible ? theme.colors.primary.dark : theme.colors.primary.default};
  border-radius: 50%;
  font-size: 21px;
  text-align: center;
  cursor: pointer;
  color: ${({ theme, isMapVisible }) => (isMapVisible ? theme.colors.primary.default : theme.colors.primary.dark)};
  box-shadow: 0 -1px 6px 0 rgba(0, 0, 0, 0.3);
  transition: top 0.3s ease-in-out, right 0.3s ease-in-out;
`;
const MapIcon = styled(FontAwesomeIcon)`
  width: 20px;
  height: 100%;
`;
